import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.35; // shorter than other minions — small hits, but frequent since it's fast
const HIT_FLASH_DURATION = 0.08;
const WOBBLE_RETARGET_INTERVAL = 0.25; // seconds between erratic heading changes
const WOBBLE_MAX_ANGLE = 0.9; // radians of max deviation from a straight line at the player

/**
 * Chaff: dies in one hit (hp is always 1 — see MinionWaveSystem._createMinion),
 * fast, and its heading wobbles erratically instead of beelining, so it reads
 * as a different texture than every other minion's straight/telegraphed
 * approach. Always spawned in clusters of SWARMLING_CLUSTER_SIZE from a
 * single wave-budget slot (see MinionWaveSystem._spawnWave) rather than one
 * at a time, so a "swarmling" pick in a wave's minionTypes means a burst of
 * easy kills, not one more medium-difficulty body.
 */
export class Swarmling {
  constructor(eventBus, { x, y, radius = 4, hp, moveSpeed, contactDamage }) {
    this.eventBus = eventBus;
    this.type = 'swarmling';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;

    this._wobbleTimer = 0;
    this._wobbleOffset = 0;
  }

  update(dt, player, _bulletPool) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    this._wobbleTimer -= dt;
    if (this._wobbleTimer <= 0) {
      this._wobbleTimer = WOBBLE_RETARGET_INTERVAL;
      this._wobbleOffset = (Math.random() * 2 - 1) * WOBBLE_MAX_ANGLE;
    }

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    const baseAngle = Math.atan2(dy, dx);
    const angle = baseAngle + this._wobbleOffset;
    this.x += Math.cos(angle) * this.moveSpeed * dt;
    this.y += Math.sin(angle) * this.moveSpeed * dt;

    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
